import { inject, Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';
/**
 * Service de surveillance des erreurs de chargement de modules (Chunks) dans une PWA Angular
 * * Rôle :
 * Ce service surveille les erreurs de navigation du Router Angular.
 * Son but unique est de détecter les échecs de chargement de fichiers JS (Chunks) qui surviennent
 * typiquement après un nouveau déploiement, lorsque l'utilisateur navigue vers un module avec lay-loading
 * dont le fichier n'existe plus.
 *
 * * Comportement :
 * - Si une erreur "ChunkLoadError" ou "Importing a module script failed" est détectée :
 * -> Il force un rechargement de la page (`window.location.reload()`).
 * -> Cela permet de récupérer la nouvelle version de l'application avec les fichiers à jour
 *
 * * Coexistence avec ApplicationInsights :
 * L'application utilise `ApplicationInsightsErrorHandler` comme gestionnaire d'erreurs global (Global ErrorHandler).
 * Ce service ne remplace PAS le gestionnaire global.
 * Il agit en complément en écoutant le flux d'événements du Router (`router.events`). Cela permet de gérer le rechargement silencieusement
 * sans perturber la télémétrie Azure pour les autres types d'erreurs.
 */

@Injectable({
  providedIn: 'root',
})
export class ChunkErrorGuardService {
  private router = inject(Router);

  constructor() {
    this.initErrorListener();
  }

  private initErrorListener(): void {
    this.router.events.pipe(filter((event): event is NavigationError => event instanceof NavigationError)).subscribe(event => {
      if (this.isChunkError(event.error)) {
        console.warn('PWA: Erreur de chargement de module détectée. Rechargement forcé pour récupérer la nouvelle version.');
        window.location.reload();
      }
    });
  }

  private isChunkError(error: any): boolean {
    if (!error) return false;

    const message = (error.message || '').toLowerCase();
    const name = error.name || '';

    // Pas de type spécifique dans les navigateurs: la détection se fait sur le message de l'erreur  et sur le nom de l'erreur
    return (
      /loading chunk [\d]+ failed/.test(message) ||
      /importing a module script failed/.test(message) ||
      /failed to fetch dynamically imported module/.test(message) ||
      name === 'ChunkLoadError' ||
      message.includes('chunkloaderror')
    );
  }
}
